import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { JANE_DEFINED_ROWS } from '@iheartjane/menu';
import { Flex, Link, Typography } from '@iheartjane/reefer';
import { Form, useFormContext } from '@iheartjane/reefer-hook-form';
import capitalize from 'lodash-es/capitalize';

import { useDebouncedTrack } from '@jane/business-admin/hooks';
import type { MenuRow } from '@jane/business-admin/types';
import {
  EventNames,
  ModalNames,
  SettingNames,
  track,
} from '@jane/business-admin/util';

import { CategoryAndSubcategorySelect } from '../../../../CategoryAndSubcategorySelect';
import { DynamicRowCard } from './DynamicRowCard';
import { OptionCardWithPill } from './OptionCardWithPill';

export const DetailsCard = ({
  defaultRowType,
  isCreateMode,
  row,
  rowProductType,
}: {
  defaultRowType?: string;
  isCreateMode: boolean;
  row?: MenuRow;
  rowProductType: any;
}) => {
  const {
    setValue,
    watch,
    formState: { isDirty },
  } = useFormContext();
  const rowType = watch('display.type');
  const menuRowTypeValue = watch('menu_row_type');
  const customDisplayName = watch('custom_display_name');
  const trackTextChange = useDebouncedTrack(1000);
  const modalName = isCreateMode
    ? ModalNames.CreateMenuRow
    : ModalNames.EditMenuRow;

  const hasSubcatAndCat = useFlag(FLAGS.scMenuRowSubcategoryAndCategory);
  const useMenuRowType = useFlag(FLAGS.cnMenuRowTypeFe);

  const editableNameRowTypes = ['specials', 'sale', 'best_selling'];

  const onClickOptionCardWithPill = (value: string) => {
    if (useMenuRowType) {
      setValue('menu_row_type', 'custom', { shouldDirty: true });
    } else {
      setValue('display.type', value, { shouldDirty: true });
    }
  };

  const subcategoryProperties = hasSubcatAndCat
    ? {
        name: 'display.subcategory',
        required: false,
        defaultValue: rowProductType.display?.subcategory || 'None',
        onChange: () => {
          track({
            event: EventNames.ModifiedSetting,
            modal_name: modalName,
            revert: false,
            setting_name: SettingNames.ProductSubcategory,
          });
        },
      }
    : {
        name: 'display.subcategory',
        defaultValue: rowProductType.display?.subcategory || 'Bottom Shelf',
        onChange: () => {
          track({
            event: EventNames.ModifiedSetting,
            modal_name: modalName,
            revert: false,
            setting_name: SettingNames.ProductSubcategory,
          });
        },
      };

  return (
    <>
      <Typography variant="header-bold" mb={32}>
        Details
      </Typography>

      <Typography mb={16}>{isCreateMode ? 'Select r' : 'R'}ow type</Typography>

      {defaultRowType && JANE_DEFINED_ROWS.includes(defaultRowType) ? (
        <DynamicRowCard row_type={defaultRowType} />
      ) : (
        <Flex gap={20} mb={24}>
          <OptionCardWithPill
            pillColor="ultralight"
            title="Custom"
            subtitle="Individually choose products."
            disabled={
              isCreateMode
                ? false
                : useMenuRowType
                  ? menuRowTypeValue !== 'custom'
                  : rowType !== 'custom'
            }
            selected={
              useMenuRowType
                ? menuRowTypeValue === 'custom'
                : rowType === 'custom'
            }
            onClick={onClickOptionCardWithPill}
            withBackground={false}
          />
          {!useMenuRowType && (
            <OptionCardWithPill
              pillColor="ultralight"
              title={
                hasSubcatAndCat ? 'Category or Subcategory' : 'Subcategory'
              }
              subtitle={
                hasSubcatAndCat
                  ? 'Create menu rows based on product category or subcategory.'
                  : 'Create menu rows based on product subcategory.'
              }
              disabled={isCreateMode ? false : rowType !== 'subcategory'}
              selected={rowType === 'subcategory'}
              onClick={onClickOptionCardWithPill}
              withBackground={false}
            />
          )}
        </Flex>
      )}

      {((useMenuRowType &&
        (menuRowTypeValue === 'custom' ||
          (menuRowTypeValue &&
            editableNameRowTypes.includes(menuRowTypeValue)))) ||
        rowType ||
        (defaultRowType && editableNameRowTypes.includes(defaultRowType))) &&
        rowProductType.display && (
          <>
            {!useMenuRowType && rowType === 'subcategory' && (
              <CategoryAndSubcategorySelect
                mb={24}
                allowAllOption={false}
                isLoading={!rowProductType.display}
                categoryProps={{
                  name: 'display.category',
                  defaultValue: rowProductType.display?.category || 'flower',
                  onChange: () => {
                    setValue('display.subcategory', 'None');

                    track({
                      event: EventNames.ModifiedSetting,
                      modal_name: modalName,
                      revert: false,
                      setting_name: SettingNames.ProductCategory,
                    });
                  },
                }}
                subcategoryProps={subcategoryProperties}
              />
            )}
            <Form.TextField
              label="Name"
              name="custom_display_name"
              required
              onChange={(value) => {
                const isRevert =
                  value === row?.custom_display_name ||
                  value === defaultRowType ||
                  value === capitalize(defaultRowType);
                const valueNotChanged = !value || isRevert;

                if (!isDirty && valueNotChanged) return;

                trackTextChange({
                  event: EventNames.ModifiedSetting,
                  modal_name: modalName,
                  revert: isRevert,
                  setting_name: SettingNames.MenuRowDisplayName,
                });
              }}
            />
            {(() => {
              const showRevertLink =
                rowType === 'dynamic' ||
                (useMenuRowType &&
                  menuRowTypeValue &&
                  editableNameRowTypes.includes(menuRowTypeValue)) ||
                (defaultRowType &&
                  editableNameRowTypes.includes(defaultRowType));

              const isSpecialsOrDeals = (name: string | undefined) => {
                if (!name) return false;
                const nameLower = name.toLowerCase();
                return nameLower === 'specials' || nameLower === 'deals';
              };

              const getDefaultDisplayName = () => {
                if (
                  defaultRowType &&
                  editableNameRowTypes.includes(defaultRowType)
                ) {
                  if (
                    defaultRowType === 'specials' &&
                    !isSpecialsOrDeals(customDisplayName)
                  ) {
                    return 'Deals';
                  }
                  return capitalize(defaultRowType);
                }
                return defaultRowType;
              };

              const shouldShowRevert = () => {
                if (!showRevertLink || !customDisplayName) return false;

                if (defaultRowType === 'specials') {
                  return !isSpecialsOrDeals(customDisplayName);
                }

                return (
                  customDisplayName !== defaultRowType &&
                  customDisplayName !== capitalize(defaultRowType)
                );
              };

              return (
                shouldShowRevert() && (
                  <Flex flexDirection="column" mt={24}>
                    <Typography variant="body">
                      Default: {getDefaultDisplayName()}
                    </Typography>
                    <Flex mt={4} mb={8}>
                      <Link
                        onClick={() =>
                          setValue(
                            'custom_display_name',
                            getDefaultDisplayName()
                          )
                        }
                      >
                        Revert to default
                      </Link>
                    </Flex>
                  </Flex>
                )
              );
            })()}
          </>
        )}
    </>
  );
};
